import { normalizarVentanas } from './franjas';
// Datos de una orden de visita armados desde un presupuesto (29-sep-2026).
// Antes había dos armados distintos: el Despachar de Presupuestos mandaba
// dirección, monto, teléfono y descripción, y el auto-despacho del asistente
// ("Visita — Cliente") mandaba solo el título → la orden llegaba al técnico
// sin dirección ni monto, y al editarla el admin veía todo vacío.
const totalPresupuesto = (p) =>
    (p?.items || []).reduce((a, i) => a + Number(i.costo || 0), 0);

export function datosOrdenDesdePresupuesto(presupuesto, { tecnicoId, fechaProgramada, horaEstimada = null, prioridad = 'NORMAL' }) {
    const p = presupuesto || {};
    const total = totalPresupuesto(p);
    // Horario a coordinar: la orden va al primer día que le sirve al cliente (no
    // "hoy"), sin hora, y las franjas quedan escritas en las instrucciones.
    const tentativa = !!p.fechaTentativa && parseVentanas(p.ventanasDisponibles).length > 0;
    const trabajo = (p.items || []).map(it => it.trabajoRealizado).filter(Boolean).join(' · ');
    // 1-nov-2026: lo que hay que hacer ya le llega al técnico en "Lo que mandó el cliente" (con las fotos de cada
    // equipo); repetirlo en Instrucciones lo mostraba dos veces. Solo queda acá el aviso del horario a coordinar.
    const descripcion = tentativa
        ? `Horario a coordinar. El cliente puede: ${resumenVentanas(p.ventanasDisponibles).join(' / ')}`
        : '';
    // El título dice qué hay que hacer y para quién ("Reparación de pérdida · Carlos"), no "Visita · Carlos"
    const queHacer = String(trabajo).split(/[.·\n]/)[0].trim().slice(0, 40);
    return {
        tecnicoId:       Number(tecnicoId),
        titulo:          `${queHacer || 'Visita'} · ${p.clienteNombre || 'Cliente'}`,
        descripcion:     descripcion || '',
        clienteId:       p.clienteId || null,
        clienteNombre:   p.clienteNombre || '',
        clienteTelefono: p.clienteTelefono || '',
        direccion:       p.sedeDireccion || p.sedeNombre || '',
        prioridad,
        fechaProgramada: (tentativa && proximaFechaVentanas(p.ventanasDisponibles)) || fechaProgramada,
        horaEstimada:    tentativa ? null : (horaEstimada || null),
        montoEstimado:   total || null,
        formaPago:       'EFECTIVO',
        presupuestoId:   p.id,
    };
}

// ── Horario "a coordinar" (fecha tentativa) ─────────────────────────────────
// ventanas = JSON [{dia:'MIERCOLES', franja:'12:00-14:00'}, …] que marcó el admin
// según lo que prefiere el cliente.
const DIA_CORTO = { LUNES: 'Lun', MARTES: 'Mar', MIERCOLES: 'Mié', JUEVES: 'Jue', VIERNES: 'Vie', SABADO: 'Sáb' };
const ORDEN_DIAS = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];
// Ventanas guardadas con las franjas viejas se pasan a las nuevas al mostrarlas (utils/franjas)
const DIA_JS = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];

export function parseVentanas(json) {
    if (!json) return [];
    if (Array.isArray(json)) return json;
    try { return JSON.parse(json) || []; } catch { return []; }
}
const parseNormalizadas = (json) => normalizarVentanas(parseVentanas(json));

// "Mié y Jue · Mediodía (12 a 14)"; varias franjas → una línea por franja
// Horario fijo (16-oct-2026): una ventana con "hora" sale como "Jue y Vie · a las 13:00 (fijo)"
export function resumenVentanas(json) {
    const vs = parseNormalizadas(json);
    const porFranja = {};
    vs.forEach(v => { const k = v.hora ? `@${v.hora}` : v.franja; (porFranja[k] = porFranja[k] || []).push(v.dia); });
    return Object.keys(porFranja).sort().map(f => {
        const ids = [...new Set(porFranja[f])].sort((a, b) => ORDEN_DIAS.indexOf(a) - ORDEN_DIAS.indexOf(b));
        // Días seguidos (3 o más) como rango: "Lun a Sáb" (10-oct-2026)
        const tramos = [];
        ids.forEach(d => {
            const ult = tramos.at(-1);
            if (ult && ORDEN_DIAS.indexOf(d) === ORDEN_DIAS.indexOf(ult.at(-1)) + 1) ult.push(d); else tramos.push([d]);
        });
        const dias = tramos.flatMap(t => t.length >= 3 ? [`${DIA_CORTO[t[0]]} a ${DIA_CORTO[t.at(-1)]}`] : t.map(d => DIA_CORTO[d] || d));
        const listaDias = dias.length > 1 ? `${dias.slice(0, -1).join(', ')} y ${dias.at(-1)}` : dias[0];
        if (f.startsWith('@')) return `${listaDias} · a las ${f.slice(1)} (horario fijo)`;
        const [desde, hasta] = f.split('-').map(h => String(Number(h.split(':')[0])));
        return `${listaDias} · de ${desde} a ${hasta} h`;
    });
}

// Próxima fecha (desde mañana) que cae en alguno de los días habilitados
export function proximaFechaVentanas(json) {
    const dias = new Set(parseVentanas(json).map(v => v.dia));
    if (dias.size === 0) return null;
    const d = new Date();
    for (let i = 1; i <= 14; i++) {
        d.setDate(d.getDate() + 1);
        if (dias.has(DIA_JS[d.getDay()])) {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
    }
    return null;
}

// Las horas se guardan como texto ("9:00", "10:30", "Mañana"): clave para ordenarlas bien
// (9:00 antes que 10:00). "Mañana"/"Tarde" a media mañana/tarde; sin hora, al final.
// Orden de la agenda del día (27-oct-2026): por hora y, en la misma franja o sin hora, por el orden guardado
export const claveAgenda = (hora, ordenDia, id = 0) => `${horaOrden(hora)}|${String(ordenDia ?? 999).padStart(3, '0')}|${String(id).padStart(9, '0')}`;

export function horaOrden(h) {
    const t = String(h || '').trim();
    const m = /^(\d{1,2}):(\d{2})/.exec(t);
    if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
    return { mañana: '11:59', tarde: '17:59' }[t.toLowerCase()] || '99';
}

// "Salgo ahora" en una visita con el día a coordinar (15-oct-2026): fija hoy y la hora actual
// (el backend controla que esté dentro de los días/franjas que marcó la empresa) y la pone En camino.
// Si hoy no corresponde, el backend responde el motivo y se elige el día a mano.
export async function salirAhoraACoordinar(api, ordenId) {
    const d = new Date();
    const pad = n => String(n).padStart(2, '0');
    const fecha = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    await api.patch(`/ordenes/${ordenId}/confirmar-horario`, { fecha, hora: `${pad(d.getHours())}:${pad(d.getMinutes())}` });
    const r = await api.patch(`/ordenes/${ordenId}/estado`, { estado: 'EN_CAMINO' });
    return r.data;
}

// Horario como rango (16-oct-2026): "09:00-18:00" = el cliente puede de 9 a 18.
export const RANGO_HORA = /^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/;
/** Texto para mostrar: "10:30", "9 a 18 h", "Mañana"… */
export function horaTexto(h) {
    const t = String(h || '').trim();
    if (!t) return '';
    const r = RANGO_HORA.exec(t);
    const corta = (x) => { const [hh, mm] = x.split(':'); return mm === '00' ? String(Number(hh)) : `${Number(hh)}:${mm}`; };
    if (r) return `${corta(r[1])} a ${corta(r[2])} h`;
    return /^\d{1,2}:\d{2}/.test(t) ? t.slice(0, 5) : t;
}

// Título de una visita sin el nombre del cliente cuando ya se muestra aparte (1-nov-2026):
// "Revisar falla · Eva" con el cliente "Eva" → "Revisar falla"
export const tituloSinCliente = (titulo, cliente) => {
    const t = String(titulo || '').trim();
    const c = String(cliente || '').trim();
    if (!t || !c) return t || null;
    const sin = t.split(' · ').filter(p => p.trim() !== c).join(' · ');
    return sin || null;
};
